import { useState, useCallback, useRef, useEffect } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

interface UseTextToSpeechProps {
  voice?: string;
  language?: string;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: string) => void;
}

interface UseTextToSpeechReturn {
  isSpeaking: boolean;
  isSupported: boolean;
  speak: (text: string) => void;
  stop: () => void;
  error: string | null;
}

export const useTextToSpeech = ({
  voice = 'en-US-JennyNeural',
  language = 'en-US',
  onStart,
  onEnd,
  onError
}: UseTextToSpeechProps = {}): UseTextToSpeechReturn => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const synthesizerRef = useRef<SpeechSDK.SpeechSynthesizer | null>(null);

  // Check if text-to-speech is supported
  useEffect(() => {
    const speechKey = process.env.REACT_APP_SPEECH_KEY;
    const speechRegion = process.env.REACT_APP_SPEECH_REGION;
    
    setIsSupported(!!(speechKey && speechRegion && typeof window !== 'undefined'));
  }, []);

  const speak = useCallback((text: string) => {
    if (!isSupported) {
      const errorMsg = 'Text-to-speech is not supported or not configured';
      setError(errorMsg);
      onError?.(errorMsg);
      return;
    }

    if (!text.trim()) {
      return;
    }

    try {
      setError(null);
      setIsSpeaking(true);
      onStart?.();

      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        process.env.REACT_APP_SPEECH_KEY!,
        process.env.REACT_APP_SPEECH_REGION!
      );

      // Set the voice
      speechConfig.speechSynthesisVoiceName = voice;
      speechConfig.speechSynthesisLanguage = language;

      // Create synthesizer
      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig);
      synthesizerRef.current = synthesizer;

      // Speak the text
      synthesizer.speakTextAsync(
        text,
        (result) => {
          setIsSpeaking(false);
          onEnd?.();
          
          if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
            // console.log('Text-to-speech completed successfully');
          } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
            const cancellationDetails = SpeechSDK.CancellationDetails.fromResult(result);
            const errorMsg = `Text-to-speech canceled: ${cancellationDetails.reason}`;
            setError(errorMsg);
            onError?.(errorMsg);
          }
        },
        (err) => {
          setIsSpeaking(false);
          const errorMsg = `Text-to-speech error: ${err}`;
          setError(errorMsg);
          onError?.(errorMsg);
          console.error('Text-to-speech error:', err);
        }
      );
    } catch (error) {
      setIsSpeaking(false);
      const errorMsg = `Failed to initialize text-to-speech: ${error}`;
      setError(errorMsg);
      onError?.(errorMsg);
      console.error('Error initializing text-to-speech:', error);
    }
  }, [isSupported, voice, language, onStart, onEnd, onError]);

  const stop = useCallback(() => {
    if (synthesizerRef.current) {
      synthesizerRef.current.close();
      synthesizerRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  return {
    isSpeaking,
    isSupported,
    speak,
    stop,
    error
  };
}; 